/** Privacy Policy copy — source: Google Doc “Privacy Policy - ALL India”, last updated 12 October 2026. */

export const PRIVACY_SEO = {
  title: "Privacy Policy | A Learning Lab India",
  description:
    "How UniHawk Education Support Services LLC collects, uses and protects personal information on the A Learning Lab India website, in line with applicable Indian data protection law.",
  path: "/privacy",
};

export const PRIVACY_LAST_UPDATED = "12 October 2026";

export const PRIVACY_INTRO = [
  "UniHawk Education Support Services LLC (“Company”, “we”, “us” or “our”) respects your privacy and recognises the importance of protecting the personal information of individuals who access or use our website, https://alearninglab.com/in (“Website”).",
  "For the purposes of this Privacy Policy, the Company is responsible for determining how and why personal information collected through the Website is used, subject to applicable data protection laws.",
  "We take your privacy seriously. Please read this Privacy Policy carefully to understand what personal information we collect, why we collect it, how we use and protect it, when we share it, and the rights you may have in relation to it.",
  "We process personal information in accordance with applicable data protection laws in India, including the Digital Personal Data Protection Act, 2023 (“DPDP Act”) and the Digital Personal Data Protection Rules, 2025 (“DPDP Rules”), to the extent applicable and as their respective provisions come into force. We also comply with other applicable Indian laws and regulations governing the collection, use, storage and disclosure of personal information, where relevant.",
  "This Privacy Policy is primarily intended for individuals accessing our Website from India, including students, prospective students, parents and legal guardians. It should be read alongside any separate consent forms, application terms or service agreements that we may provide to you.",
];

export const PRIVACY_CONTENTS = [
  { num: 1, title: "What this Privacy Policy applies to", id: "what-this-privacy-policy-applies-to" },
  { num: 2, title: "Personal information we collect", id: "personal-information-we-collect" },
  { num: 3, title: "How we collect personal information", id: "how-we-collect-personal-information" },
  { num: 4, title: "How and why we use personal information", id: "how-and-why-we-use-personal-information" },
  { num: 5, title: "Marketing communications", id: "marketing-communications" },
  { num: 6, title: "Who we share personal information with", id: "who-we-share-personal-information-with" },
  { num: 7, title: "How long we retain personal information", id: "how-long-we-retain-personal-information" },
  { num: 8, title: "International transfers of personal information", id: "international-transfers-of-personal-information" },
  { num: 9, title: "Cookies and other tracking technologies", id: "cookies-and-other-tracking-technologies" },
  { num: 10, title: "Your privacy rights", id: "your-privacy-rights" },
  { num: 11, title: "Keeping your personal information secure", id: "keeping-your-personal-information-secure" },
  { num: 12, title: "Changes to this Privacy Policy", id: "changes-to-this-privacy-policy" },
  { num: 13, title: "How to contact us", id: "how-to-contact-us" },
];

/** @typedef {{ type: 'p', text: string }} PrivacyParagraph */
/** @typedef {{ type: 'ul' | 'ol', items: string[] }} PrivacyList */
/** @typedef {{ type: 'h3', text: string }} PrivacySubheading */
/** @typedef {PrivacyParagraph | PrivacyList | PrivacySubheading} PrivacyBlock */

/** @type {{ id: string, title: string, blocks: PrivacyBlock[] }[]} */
export const PRIVACY_SECTIONS = [
  {
    id: "what-this-privacy-policy-applies-to",
    title: "1. What this Privacy Policy applies to",
    blocks: [
      {
        type: "p",
        text: "This Privacy Policy applies to your use of our Website and the personal information we collect through it, including when you submit an enquiry, request information about our educational services, register your interest in a programme, complete an online form or contact us through the communication channels provided on the Website.",
      },
      {
        type: "p",
        text: "It also applies to personal information we receive through related online and offline interactions where those interactions are connected to our Website or the services you request from us.",
      },
      {
        type: "p",
        text: "This Privacy Policy does not automatically apply to third-party websites, platforms or services that may be linked to or accessible through our Website. Those third parties are responsible for their own privacy practices, and we encourage you to review their privacy policies before providing personal information.",
      },
    ],
  },
  {
    id: "personal-information-we-collect",
    title: "2. Personal information we collect",
    blocks: [
      {
        type: "p",
        text: "The personal information we collect depends on how you interact with us and the services you request. It may include the following categories:",
      },
      {
        type: "ul",
        items: [
          "Identity information: Your full name, date of birth and other details reasonably required to identify you.",
          "Parent or guardian information: The name and contact details of a parent or legal guardian, where relevant, particularly when interacting with students under the age of 18.",
          "Contact information: Email address, telephone number, postal address and other contact details you choose to provide.",
          "Academic information: School details, educational history, grades, transcripts, report cards, test scores, academic interests and other information relevant to educational guidance or programme applications.",
          "Application and supporting documents: Documents submitted in connection with an application or service, which may include identification documents, passport copies, academic records, personal statements, recommendation letters or other supporting information, where required for the relevant service.",
          "Service-related information: Details of enquiries, consultations, programmes of interest, services requested or received, application progress, communications and feedback.",
          "Consent and authorisation records: Records of permissions, declarations, parental or guardian consents and authorisations provided in connection with our services.",
          "Technical and usage information: Internet Protocol (IP) address, browser type, operating system, device information, approximate location derived from technical data, pages visited, time spent on the Website, referral sources and interactions with Website links.",
          "Marketing and engagement information: Your communication preferences, responses to surveys, participation in campaigns and information about how you interact with our communications.",
          "Other information you provide: Any additional information you voluntarily submit through forms, emails, telephone calls, social media channels or other communication methods.",
        ],
      },
      {
        type: "p",
        text: "We seek to collect only the information reasonably necessary for the purposes described in this Privacy Policy.",
      },
      {
        type: "p",
        text: "Where we request personal information that is optional, we will indicate this where appropriate. If particular information is necessary to respond to your enquiry, process an application or deliver a requested service, not providing it may limit our ability to assist you.",
      },
      {
        type: "p",
        text: "Please do not submit sensitive or unnecessary personal information unless it is specifically requested for a legitimate purpose connected with the service you are seeking.",
      },
    ],
  },
  {
    id: "how-we-collect-personal-information",
    title: "3. How we collect personal information",
    blocks: [
      { type: "p", text: "We may collect personal information through the following methods." },
      { type: "h3", text: "3.1 Information you provide directly" },
      { type: "p", text: "We collect information directly from you when you:" },
      {
        type: "ul",
        items: [
          "Submit an enquiry or contact form through our Website.",
          "Request information about our educational services, programmes or consultations.",
          "Register your interest in a programme or request assistance with an application.",
          "Complete registration, application, consent or authorisation forms.",
          "Contact us by email, telephone, social media or other communication channels.",
          "Participate in surveys, events, exhibitions, school information sessions or promotional activities.",
          "Provide feedback or communicate with our team about our services.",
        ],
      },
      { type: "h3", text: "3.2 Information collected automatically" },
      {
        type: "p",
        text: "When you visit our Website, certain technical information and browsing activity may be collected automatically through server logs, cookies and similar technologies. This information helps us maintain Website functionality, understand visitor behaviour, identify technical issues and improve the user experience.",
      },
      {
        type: "p",
        text: "You can find further information in the section titled “Cookies and other tracking technologies”.",
      },
      { type: "h3", text: "3.3 Information received from other sources" },
      { type: "p", text: "Where appropriate and lawful, we may receive personal information from:" },
      {
        type: "ul",
        items: [
          "A parent or legal guardian acting on behalf of a student.",
          "Schools, educational institutions or authorised representatives.",
          "Universities or other institutions involved in an application you have authorised us to support.",
          "Service providers and partners assisting us with educational services or application processes.",
          "Advertising, social media and digital marketing platforms.",
          "Event organisers, educational fairs or campaign partners.",
          "Publicly available sources, where relevant to a legitimate purpose.",
        ],
      },
      {
        type: "p",
        text: "Where personal information is provided by another person on your behalf, we expect that person to have the necessary authority to share it with us. Where required by applicable law, we will provide the relevant notice or obtain the necessary consent.",
      },
    ],
  },
  {
    id: "how-and-why-we-use-personal-information",
    title: "4. How and why we use personal information",
    blocks: [
      {
        type: "p",
        text: "We use personal information only for legitimate purposes connected with our Website, educational services, business operations and applicable legal obligations.",
      },
      {
        type: "p",
        text: "Depending on the circumstances and the applicable law, the relevant basis for processing may include your consent, steps taken at your request before entering into an agreement, performance of an agreement, compliance with a legal obligation or another basis permitted by applicable law.",
      },
      { type: "p", text: "We may use your personal information for the following purposes:" },
      {
        type: "ol",
        items: [
          "Responding to enquiries: To answer your questions, provide requested information, arrange consultations and communicate with you about our services.",
          "Providing educational services: To deliver personalised educational guidance, counselling, tutoring, test preparation, academic support and university admissions assistance where these services are requested.",
          "Processing applications: To assist with preparing, reviewing and submitting applications to universities, educational institutions or programme providers, where you have requested and authorised us to do so.",
          "Verifying information: To verify relevant identity, contact, academic or application details where reasonably necessary for the requested service or required by law.",
          "Communicating about services: To send service-related updates, application information, appointment confirmations, changes to our terms or policies and other important notices.",
          "Improving the Website: To understand how visitors use our Website, improve its content and functionality, diagnose technical issues and enhance accessibility and performance.",
          "Personalising your experience: To provide relevant information about services or programmes based on your stated interests and preferences, where permitted by applicable law.",
          "Marketing and engagement: To inform you about educational opportunities, programmes, events, updates and promotional offers in accordance with your preferences and applicable marketing and data protection requirements.",
          "Maintaining records: To maintain student, customer, enquiry, application, consent and service records for legitimate operational purposes and to meet applicable legal requirements.",
          "Security and fraud prevention: To protect our Website, systems, services and users, detect suspicious activity, investigate suspected misuse and prevent fraud or unauthorised access.",
          "Legal and regulatory compliance: To comply with applicable laws, regulations, lawful requests from authorities, record-keeping requirements and other legal obligations.",
          "Dispute resolution and legal claims: To establish, exercise or defend legal rights and respond to complaints, claims or legal proceedings.",
          "Business administration: To conduct internal reporting, financial administration, audits, service evaluation and other legitimate business activities.",
          "Business restructuring: To facilitate a merger, acquisition, restructuring, financing, sale of assets or other corporate transaction, subject to appropriate safeguards and applicable law.",
          "Other disclosed purposes: To use personal information for another purpose that has been communicated to you and is permitted by applicable law.",
        ],
      },
      {
        type: "p",
        text: "Where we rely on your consent, you may withdraw it in accordance with applicable law. Withdrawal does not affect the lawfulness of processing carried out before the withdrawal. However, withdrawing consent may affect our ability to provide certain services if the information is necessary for those services and no other lawful basis for processing applies.",
      },
    ],
  },
  {
    id: "marketing-communications",
    title: "5. Marketing communications",
    blocks: [
      {
        type: "p",
        text: "We may use your contact information to send you updates about our educational services, programmes, events, new offerings and promotional activities, subject to applicable law and your communication preferences.",
      },
      {
        type: "p",
        text: "Where consent is required for marketing communications, we will obtain it before sending those communications. We will not treat your request for information or your use of our services as automatic consent to all forms of promotional communication where separate consent is required.",
      },
      {
        type: "p",
        text: "You can opt out of marketing communications at any time by following the unsubscribe instructions in the communication or contacting us at info@alearninglab.com/in.",
      },
      {
        type: "p",
        text: "Opting out of marketing will not prevent us from sending essential service-related communications, such as responses to your enquiries, application updates or notices concerning services you have requested.",
      },
      { type: "p", text: "We may ask you to review or update your marketing preferences where appropriate." },
      {
        type: "p",
        text: "We do not sell your personal information to third parties. We do not share your personal information with unrelated third parties for their independent marketing purposes without the appropriate permission or other lawful basis.",
      },
      { type: "h3", text: "Student achievements and promotional materials" },
      {
        type: "p",
        text: "We may wish to feature student achievements, testimonials, photographs, videos, projects or success stories in our marketing materials, presentations, social media channels or other publications.",
      },
      {
        type: "p",
        text: "We will obtain the necessary permission before using identifiable student information or promotional materials where required by applicable law. For students under the age of 18, we will obtain consent from a parent or legal guardian where required, along with the student's agreement where appropriate.",
      },
      {
        type: "p",
        text: "Providing such consent is voluntary and is not a condition of receiving our educational services unless expressly stated for a specific activity.",
      },
    ],
  },
  {
    id: "who-we-share-personal-information-with",
    title: "6. Who we share personal information with",
    blocks: [
      {
        type: "p",
        text: "We may share personal information where necessary to provide our services, fulfil your requests, operate our Website, meet legal obligations or protect our legitimate rights, subject to applicable law.",
      },
      { type: "p", text: "Depending on the circumstances, recipients may include:" },
      {
        type: "ul",
        items: [
          "Our group companies and affiliates: Where they assist with service delivery, administration, customer support or related business operations.",
          "Universities and educational institutions: Where you have authorised us to support or submit an application on your behalf.",
          "Programme providers and educational partners: Where sharing is necessary to register you for, or deliver, a programme or service you have requested.",
          "Schools, parents and legal guardians: Where appropriate and authorised, or where disclosure is required or permitted by law.",
          "Service providers: Including website hosting providers, IT support providers, cloud storage providers, communication platforms, customer relationship management systems and application management providers.",
          "Marketing and analytics providers: To support advertising, campaign management, Website analytics and communication delivery, subject to applicable law and relevant preferences.",
          "Professional advisers: Including lawyers, accountants, auditors, financial advisers and other professional consultants who are subject to appropriate confidentiality obligations.",
          "Government authorities and regulators: Where disclosure is required by applicable law, a lawful order or a valid request from an authorised authority.",
          "Law enforcement and courts: Where necessary to comply with legal obligations, investigate suspected unlawful activity or establish, exercise or defend legal claims.",
          "Business transaction parties: In connection with a merger, acquisition, restructuring, financing or sale of all or part of our business, subject to appropriate confidentiality and data protection safeguards.",
        ],
      },
      {
        type: "p",
        text: "We limit access to personal information within our organisation to individuals who need it for their responsibilities.",
      },
      {
        type: "p",
        text: "Where third-party service providers process personal information on our behalf, we take reasonable steps to ensure that appropriate contractual, organisational and technical safeguards are in place. We expect such providers to use the information only for authorised purposes and to protect it against unauthorised access, use or disclosure.",
      },
      {
        type: "p",
        text: "We may also disclose personal information where you have authorised us to do so or where disclosure is otherwise permitted or required by applicable law.",
      },
      { type: "p", text: "We do not sell personal information as part of our business operations." },
    ],
  },
  {
    id: "how-long-we-retain-personal-information",
    title: "7. How long we retain personal information",
    blocks: [
      {
        type: "p",
        text: "We retain personal information only for as long as reasonably necessary for the purposes for which it was collected, including providing our services, maintaining records, meeting legal obligations, resolving disputes and protecting our legal rights.",
      },
      {
        type: "p",
        text: "The retention period depends on the type of information, the nature of the service, the purpose of processing, applicable legal requirements and whether any dispute or legal claim requires the information to be retained.",
      },
      { type: "p", text: "For example:" },
      {
        type: "ul",
        items: [
          "Enquiry and communication records may be retained for a reasonable period to respond to enquiries and manage follow-up communications.",
          "Student and application records may be retained for the duration of the relevant service and for an appropriate period afterwards.",
          "Consent and authorisation records may be retained for as long as necessary to demonstrate compliance with applicable requirements.",
          "Financial, accounting and legal records may be retained for the period required by applicable law.",
          "Technical and Website analytics information may be retained according to the relevant system settings and operational requirements.",
        ],
      },
      {
        type: "p",
        text: "When personal information is no longer required, we will take reasonable steps to delete it, securely dispose of it or anonymise it, unless retention is required or permitted by applicable law.",
      },
    ],
  },
  {
    id: "international-transfers-of-personal-information",
    title: "8. International transfers of personal information",
    blocks: [
      {
        type: "p",
        text: "The Company may use service providers, technology platforms, group companies or educational partners from across the world.",
      },
      {
        type: "p",
        text: "As a result, your personal information may be processed or transferred outside India, depending on the services you use and the arrangements required to provide them.",
      },
      {
        type: "p",
        text: "Where applicable Indian law regulates an international transfer, we will take the steps required under that law. This may include complying with restrictions imposed by the Government of India, meeting applicable contractual requirements and implementing appropriate safeguards for the information transferred.",
      },
      { type: "p", text: "We will not transfer personal information in a manner that is prohibited by applicable law." },
      {
        type: "p",
        text: "The privacy and data protection laws of the country receiving your information may differ from those applicable in India. Where required, we will take appropriate steps to protect your information and ensure that the transfer and subsequent processing comply with applicable legal requirements.",
      },
      {
        type: "p",
        text: "For further information about international transfers relevant to your personal information, you may contact us using the details in the “How to contact us” section.",
      },
    ],
  },
  {
    id: "cookies-and-other-tracking-technologies",
    title: "9. Cookies and other tracking technologies",
    blocks: [
      {
        type: "p",
        text: "Cookies are small text files placed on your computer, smartphone or other device when you visit a website. We may use cookies and similar technologies to help our Website function, remember preferences, understand visitor activity and improve the services we provide.",
      },
      { type: "p", text: "The technologies we use may include:" },
      {
        type: "ul",
        items: [
          "Cookies and browser storage: These technologies may remember your preferences, support Website functionality and help us understand how visitors interact with our Website.",
          "Analytics technologies: These may collect information about Website visits, page views, referral sources and interactions to help us assess performance and improve the user experience.",
          "Pixels and web beacons: These small electronic elements may help us measure Website activity, understand whether communications have been opened and evaluate the performance of certain campaigns.",
          "Session and persistent cookies: Session cookies generally expire when you close your browser, while persistent cookies remain on your device for a specified period or until deleted.",
        ],
      },
      {
        type: "p",
        text: "Some cookies may be necessary for the Website to function, while others may support analytics, personalisation or marketing.",
      },
      {
        type: "p",
        text: "Where required by applicable law, we will obtain consent before placing or using non-essential cookies or similar tracking technologies. You may also be able to manage cookies through your browser settings or any cookie preference tool made available on the Website.",
      },
      {
        type: "p",
        text: "Disabling certain cookies may affect the availability or functionality of some Website features.",
      },
      {
        type: "p",
        text: "Third-party services integrated into our Website may use their own cookies or tracking technologies, subject to their respective privacy policies and applicable law.",
      },
    ],
  },
  {
    id: "your-privacy-rights",
    title: "10. Your privacy rights",
    blocks: [
      {
        type: "p",
        text: "Subject to applicable law and the commencement of the relevant legal provisions, you may have rights in relation to your personal information.",
      },
      { type: "p", text: "These rights may include:" },
      {
        type: "ul",
        items: [
          "Right to access information: To request information about the personal information we process about you and how it is being used, to the extent provided by applicable law.",
          "Right to correction and updating: To request correction of inaccurate or incomplete personal information and the updating of information where appropriate.",
          "Right to erasure: To request deletion of personal information where the applicable legal requirements are met and retention is not otherwise required or permitted by law.",
          "Right to withdraw consent: Where processing is based on consent, to withdraw that consent in accordance with applicable law. Withdrawal will not affect processing lawfully carried out before the withdrawal.",
          "Right to raise a grievance: To raise concerns or complaints regarding our handling of personal information through the contact details provided below and through any applicable grievance mechanism.",
          "Right to nominate another individual: Where available under applicable law, to nominate another individual to exercise specified rights in the event of your death or incapacity.",
          "Right to seek a remedy: To seek a remedy or approach the relevant authority or tribunal where available under applicable law.",
        ],
      },
      {
        type: "p",
        text: "These rights are subject to applicable conditions, exceptions and limitations. We may need to verify your identity before responding to a request and may request additional information where reasonably necessary to do so.",
      },
      {
        type: "p",
        text: "Where a request cannot be fulfilled, we will explain the position to the extent required by applicable law.",
      },
      { type: "h3", text: "How to exercise your rights" },
      {
        type: "p",
        text: "To make a privacy-related request or raise a concern, please contact us at info@alearninglab.com/in.",
      },
      { type: "p", text: "Please include:" },
      {
        type: "ul",
        items: [
          "Your full name and contact details.",
          "Sufficient information to help us identify the personal information or interaction concerned.",
          "A clear description of the request or right you wish to exercise.",
          "Any additional information reasonably required to verify your identity or authority to act on behalf of another person.",
        ],
      },
      {
        type: "p",
        text: "We will review and respond to your request within the time period required by applicable law.",
      },
      {
        type: "p",
        text: "If you are dissatisfied with our response, you may pursue the remedies available under applicable law.",
      },
      { type: "h3", text: "Children’s privacy" },
      {
        type: "p",
        text: "Our Website and educational services may be used by students under the age of 18. We recognise the importance of protecting children's personal information and expect parents or legal guardians to be involved where appropriate.",
      },
      {
        type: "p",
        text: "Where required by applicable law, we will obtain verifiable consent from a parent or legal guardian before processing a child's personal information. We will also comply with applicable requirements concerning children's personal information, including any restrictions relating to tracking, behavioural monitoring or targeted advertising.",
      },
      {
        type: "p",
        text: "We aim to collect only information reasonably necessary for the relevant educational or service-related purpose and to handle it with appropriate care.",
      },
      {
        type: "p",
        text: "Parents or legal guardians who have questions about a child's personal information or wish to make a request concerning that information may contact us using the details below.",
      },
    ],
  },
  {
    id: "keeping-your-personal-information-secure",
    title: "11. Keeping your personal information secure",
    blocks: [
      {
        type: "p",
        text: "We take reasonable technical, organisational and administrative measures to protect personal information against unauthorised access, disclosure, alteration, loss, misuse or destruction.",
      },
      {
        type: "p",
        text: "Depending on the nature of the information and the risks involved, these measures may include access controls, restricted permissions, secure information systems, confidentiality obligations, appropriate service-provider arrangements and periodic reviews of our security practices.",
      },
      {
        type: "p",
        text: "Access to personal information is limited to individuals and service providers who require it for authorised purposes.",
      },
      {
        type: "p",
        text: "Although we take reasonable steps to safeguard personal information, no website, electronic transmission or storage system can be guaranteed to be completely secure.",
      },
      {
        type: "p",
        text: "We maintain procedures for addressing suspected personal data breaches and will notify affected individuals and relevant authorities where required by applicable law.",
      },
      {
        type: "p",
        text: "You are also responsible for taking reasonable precautions when sharing information online and for ensuring that the contact details you provide are accurate.",
      },
    ],
  },
  {
    id: "changes-to-this-privacy-policy",
    title: "12. Changes to this Privacy Policy",
    blocks: [
      {
        type: "p",
        text: "We may update this Privacy Policy from time to time to reflect changes in our services, Website functionality, data processing practices, applicable laws or regulatory requirements.",
      },
      {
        type: "p",
        text: "Any revised version will be published on this page, together with an updated “Last updated” date.",
      },
      {
        type: "p",
        text: "Where required by applicable law, or where a change materially affects the way we process your personal information, we will provide an appropriate notice or obtain any additional consent that may be required.",
      },
      {
        type: "p",
        text: "We encourage you to review this Privacy Policy periodically to remain informed about how we handle personal information.",
      },
      {
        type: "p",
        text: "Changes will take effect from the date stated in the updated policy or, where no separate date is stated, from the date of publication, subject to applicable law.",
      },
    ],
  },
  {
    id: "how-to-contact-us",
    title: "13. How to contact us",
    blocks: [
      {
        type: "p",
        text: "If you have questions about this Privacy Policy, wish to exercise a privacy right, need further information about how we handle personal information or wish to raise a complaint, please contact us using the details below.",
      },
    ],
  },
];

export const PRIVACY_CONTACT = {
  company: "UniHawk Education Support Services LLC",
  website: "https://alearninglab.com/in",
  email: "info@alearninglab.com/in",
  telephone: "+91 8874200200",
  address: "104, First Floor, BPTP Park Centra, Sector 30, Gurgaon, 122001.",
};
